import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Car, Check, X, MapPin, Phone, Mail, RefreshCw, Clock,
  CalendarRange, AlertTriangle, MessageSquare, ChevronDown, ChevronUp,
  UserPlus, Edit2, Trash2, Users, Send, CalendarPlus,
} from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/dates";
import { errMsg } from "@/lib/errors";

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  pending_host_approval: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
  completed: "bg-emerald-200 text-emerald-900 border-emerald-400",
};

const STATUS_LABEL = {
  pending: "In attesa",
  pending_host_approval: "In attesa",
  approved: "Confermata",
  rejected: "Rifiutata",
  completed: "Effettuata",
};

// Build wa.me link with driver phone + pre-filled trip info (text encoded)
const buildWhatsAppLink = (item) => {
  if (!item?.driver_phone) return null;
  const phone = String(item.driver_phone).replace(/[^\d+]/g, "").replace(/^\+/, "");
  const trips = (item.trips || []).map((t) => {
    const from = t.from || t.origin || "—";
    const to = t.to || t.destination || "—";
    return `• ${from} → ${to} · ${t.date || "—"} ${t.time || "—"} · ${t.passengers || 1} pax`;
  }).join("\n");
  const lines = [
    "Ciao 👋, ti inoltro una corsa navetta confermata per B&B Emmanuel:",
    "",
    `Cliente: ${item.guest_name || "—"}`,
    item.guest_phone ? `Telefono cliente: ${item.guest_phone}` : null,
    "",
    "Tratte:",
    trips,
    "",
    "Aggiungi al calendario qui sotto 👇",
  ].filter(Boolean).join("\n");
  return `https://wa.me/${phone}?text=${encodeURIComponent(lines)}`;
};

// Format YYYY-MM-DD + HH:MM into a Google Calendar template URL (1h duration default)
const buildGoogleCalendarLink = (trip, item) => {
  if (!trip?.date || !trip?.time) return null;
  const [hh, mm] = (trip.time || "00:00").split(":");
  const start = new Date(`${trip.date}T${(hh || "00").padStart(2, "0")}:${(mm || "00").padStart(2, "0")}:00`);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  const from = trip.from || trip.origin || "—";
  const to = trip.to || trip.destination || "—";
  const text = encodeURIComponent(`Navetta B&B Emmanuel · ${from} → ${to}`);
  const details = encodeURIComponent(
    `Cliente: ${item?.guest_name || "—"}\nTelefono: ${item?.guest_phone || "—"}\nPasseggeri: ${trip.passengers || 1}\nBagagli: ${trip.luggage || 0}`
  );
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
};

// Build a downloadable .ics file (Apple Calendar)
const buildIcsDataUrl = (trip, item) => {
  if (!trip?.date || !trip?.time) return null;
  const [hh, mm] = (trip.time || "00:00").split(":");
  const start = new Date(`${trip.date}T${(hh || "00").padStart(2, "0")}:${(mm || "00").padStart(2, "0")}:00`);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  const from = trip.from || trip.origin || "—";
  const to = trip.to || trip.destination || "—";
  const summary = `Navetta B&B Emmanuel · ${from} → ${to}`;
  const description = `Cliente: ${item?.guest_name || "—"}\\nTelefono: ${item?.guest_phone || "—"}\\nPasseggeri: ${trip.passengers || 1}`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//B&B Emmanuel//Navetta//IT",
    "BEGIN:VEVENT",
    `UID:shuttle-${item?.id}-${trip.date}-${trip.time}@emmanuelbeb.com`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${from}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
};

export default function ShuttleApprovalsTab() {
  const [data, setData] = useState({ bookings: [], standalone: [], counts: {} });
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [decision, setDecision] = useState(null); // { item, action }
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [detailItem, setDetailItem] = useState(null); // when user clicks a card
  const [showDriversManager, setShowDriversManager] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/shuttle-pending"),
        api.get("/admin/drivers"),
      ]);
      setData(r1.data);
      setDrivers(r2.data?.drivers || []);
    } catch {
      toast.error("Errore caricamento richieste navetta");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const all = useMemo(() => {
    const combined = [
      ...data.bookings.map((b) => ({ ...b, source: "booking" })),
      ...data.standalone.map((s) => ({ ...s, source: "standalone" })),
    ];
    if (filter === "pending") {
      return combined.filter((x) => x.status === "pending" || x.status === "pending_host_approval");
    }
    if (filter === "approved") return combined.filter((x) => x.status === "approved");
    if (filter === "rejected") return combined.filter((x) => x.status === "rejected");
    return combined;
  }, [data, filter]);

  // ALERT: upcoming shuttle pickups in the next 24h (approved only)
  const upcoming24h = useMemo(() => {
    const now = new Date();
    const in24 = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const items = [];
    for (const it of [...data.bookings, ...data.standalone]) {
      if (it.status !== "approved") continue;
      for (const t of (it.trips || [])) {
        if (!t.date || !t.time) continue;
        const [hh, mm] = (t.time || "00:00").split(":");
        const dt = new Date(`${t.date}T${(hh || "00").padStart(2, "0")}:${(mm || "00").padStart(2, "0")}:00`);
        if (dt >= now && dt <= in24) {
          items.push({ ...it, _trip: t, _when: dt });
        }
      }
    }
    items.sort((a, b) => a._when - b._when);
    return items;
  }, [data]);

  const openDecision = (item, action) => {
    setNote("");
    setSelectedDriverId(item.driver_id || "");
    setDecision({ item, action });
    setDetailItem(null);
  };

  const submit = async () => {
    if (!decision) return;
    let driverPayload = {};
    if (decision.action === "approve") {
      const d = drivers.find((x) => x.id === selectedDriverId);
      if (!d) {
        toast.error("Seleziona un autista dalla rubrica prima di confermare");
        return;
      }
      driverPayload = {
        driver_id: d.id,
        driver_name: d.name,
        driver_phone: d.phone,
        driver_vehicle: d.vehicle || null,
      };
    }
    setSubmitting(true);
    try {
      const body = { action: decision.action, note: note.trim() || null, ...driverPayload };
      if (decision.item.source === "booking") {
        await api.post(`/admin/bookings/${decision.item.code}/shuttle/approve`, body);
      } else {
        await api.post(`/admin/shuttle-requests/${decision.item.id}/approve`, body);
      }
      toast.success(decision.action === "approve" ? "Navetta confermata · email inviata al cliente" : "Richiesta rifiutata · email inviata");
      setDecision(null);
      setNote("");
      setSelectedDriverId("");
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="shuttle-approvals-tab">
      {/* HEADER + FILTERS */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
            <Car className="w-5 h-5" /> Richieste Navetta
          </h3>
          <p className="text-xs text-[#635F59] mt-1">
            Approva o rifiuta le richieste navetta. Il cliente riceve un'email automatica con i contatti dell'autista.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="outline">
            {(data.counts?.pending_bookings || 0) + (data.counts?.pending_standalone || 0)} da approvare
          </Badge>
          <div className="flex gap-1 bg-[#FDFBF7] rounded-lg p-1 border border-[#E8E2D9]">
            {[
              { id: "pending", label: "In attesa" },
              { id: "approved", label: "Approvate" },
              { id: "rejected", label: "Rifiutate" },
              { id: "all", label: "Tutte" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`shuttle-filter-${f.id}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                  filter === f.id ? "bg-[#1E3A5F] text-white" : "text-[#635F59] hover:bg-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button onClick={refresh} variant="outline" size="sm" data-testid="shuttle-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
          </Button>
          <Button
            onClick={() => setShowDriversManager((v) => !v)}
            variant="outline"
            size="sm"
            data-testid="drivers-toggle"
          >
            <Users className="w-4 h-4 mr-1" /> Rubrica autisti ({drivers.length})
            {showDriversManager ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
        </div>
      </div>

      {/* DRIVERS MANAGER */}
      {showDriversManager && (
        <DriversManager drivers={drivers} onChange={refresh} />
      )}

      {/* UPCOMING 24H ALERT (for the admin) */}
      {upcoming24h.length > 0 && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-xl p-4"
          data-testid="shuttle-upcoming-alert"
        >
          <div className="flex items-center gap-2 mb-2 text-amber-900 font-semibold">
            <AlertTriangle className="w-4 h-4" />
            Navette nelle prossime 24h ({upcoming24h.length})
          </div>
          <ul className="text-sm text-amber-900 space-y-1">
            {upcoming24h.map((u, i) => (
              <li key={`${u.id}-${u._trip.date}-${u._trip.time}-${i}`} className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{u._trip.date} {u._trip.time}</span>
                <span>· {u._trip.from || u._trip.origin} → {u._trip.to || u._trip.destination}</span>
                <span className="text-amber-700">· {u.guest_name}</span>
                {u.driver_name && <span className="text-amber-700">· 🚖 {u.driver_name}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && <div className="bg-white border border-[#E8E2D9] rounded-xl p-8 text-center text-[#635F59]">Caricamento…</div>}

      {!loading && all.length === 0 && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-10 text-center text-[#635F59]" data-testid="shuttle-empty">
          Nessuna richiesta da mostrare con i filtri attuali.
        </div>
      )}

      <div className="grid gap-4">
        {all.map((it) => (
          <ShuttleCard
            key={`${it.source}-${it.id}`}
            item={it}
            onCardClick={() => setDetailItem(it)}
            onApprove={(e) => { e.stopPropagation(); openDecision(it, "approve"); }}
            onReject={(e) => { e.stopPropagation(); openDecision(it, "reject"); }}
            onComplete={async (item) => {
              if (item.source === "booking") {
                toast.info("Apri la prenotazione per gestire questa navetta.");
                return;
              }
              try {
                await api.post(`/admin/shuttle-requests/${item.id}/complete`);
                toast.success("Segnata come effettuata");
                await refresh();
              } catch (err) {
                toast.error(errMsg(err, "Errore"));
              }
            }}
            onArchive={async (item) => {
              if (item.source === "booking") {
                toast.info("Le navette legate a una prenotazione non si archiviano singolarmente.");
                return;
              }
              if (!window.confirm("Archiviare questa richiesta? La rimuoverà dalla lista visibile.")) return;
              try {
                await api.post(`/admin/shuttle-requests/${item.id}/archive`);
                toast.success("Archiviata");
                await refresh();
              } catch (err) {
                toast.error(errMsg(err, "Errore"));
              }
            }}
            onDelete={async (item) => {
              if (!window.confirm(`Eliminare DEFINITIVAMENTE la richiesta navetta di ${item.guest_name || "—"}? Non si potrà recuperare.`)) return;
              try {
                await api.delete(`/admin/shuttle-requests/${item.id}`);
                toast.success("Eliminata");
                await refresh();
              } catch (err) {
                toast.error(errMsg(err, "Errore"));
              }
            }}
          />
        ))}
      </div>

      {/* DETAIL DIALOG (when card is clicked) */}
      <Dialog open={!!detailItem} onOpenChange={(o) => { if (!o) setDetailItem(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shuttle-detail-dialog">
          {detailItem && (
            <ShuttleDetailView
              item={detailItem}
              onApprove={() => openDecision(detailItem, "approve")}
              onReject={() => openDecision(detailItem, "reject")}
              onClose={() => setDetailItem(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* DECISION DIALOG */}
      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) { setDecision(null); setNote(""); setSelectedDriverId(""); } }}>
        <DialogContent className="max-w-lg" data-testid="shuttle-decision-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decision?.action === "approve" ? (
                <><Check className="w-5 h-5 text-emerald-600" /> Conferma navetta</>
              ) : (
                <><AlertTriangle className="w-5 h-5 text-rose-600" /> Rifiuta richiesta</>
              )}
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="space-y-3 text-sm">
              <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3">
                <div><b>Cliente:</b> {decision.item.guest_name}</div>
                <div className="text-xs text-[#635F59]">{decision.item.guest_email}</div>
                <div className="mt-2 text-xs">
                  <b>{(decision.item.trips || []).length} tratta/e</b>
                </div>
              </div>

              {decision.action === "approve" && (
                <div>
                  <label className="text-xs text-[#635F59] mb-1 block font-semibold">
                    Autista (obbligatorio) — la rubrica salva i contatti per il riutilizzo
                  </label>
                  <select
                    value={selectedDriverId}
                    onChange={(e) => setSelectedDriverId(e.target.value)}
                    data-testid="driver-select"
                    className="w-full h-10 border border-[#E8E2D9] rounded-md px-3 text-sm bg-white"
                  >
                    <option value="">— Seleziona un autista —</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name} · {d.phone}{d.vehicle ? ` · ${d.vehicle}` : ""}
                      </option>
                    ))}
                  </select>
                  {drivers.length === 0 && (
                    <p className="text-xs text-rose-600 mt-1">
                      Nessun autista in rubrica. Aggiungine uno con il bottone <b>Rubrica autisti</b> in alto.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="text-xs text-[#635F59] mb-1 block">
                  Messaggio per il cliente (opzionale){decision.action === "reject" && " — motivo del rifiuto"}
                </label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder={
                    decision.action === "approve"
                      ? "Es. Vi aspettiamo alle 10:00 in aeroporto. Cerca il furgone blu."
                      : "Es. Mi spiace, in quel giorno il furgone non è disponibile."
                  }
                  data-testid="shuttle-decision-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDecision(null); setNote(""); setSelectedDriverId(""); }} disabled={submitting}>Annulla</Button>
            <Button
              onClick={submit}
              disabled={submitting || (decision?.action === "approve" && !selectedDriverId)}
              data-testid="shuttle-decision-confirm"
              className={decision?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
            >
              {submitting ? "Invio…" : (decision?.action === "approve" ? "Conferma e invia email" : "Rifiuta e invia email")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ShuttleCard = ({ item, onCardClick, onApprove, onReject, onComplete, onArchive, onDelete }) => {
  const isPending = item.status === "pending" || item.status === "pending_host_approval";
  const isCompleted = item.status === "completed";
  const stop = (e) => { e.stopPropagation(); };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCardClick(); }}
      className={`bg-white border rounded-xl p-5 cursor-pointer hover:border-[#AC5D45] hover:shadow-md transition ${isPending ? "border-amber-300" : "border-[#E8E2D9]"}`}
      data-testid={`shuttle-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className={STATUS_BADGE[item.status] || "bg-slate-100"} variant="outline">
              {STATUS_LABEL[item.status] || item.status}
            </Badge>
            {item.source === "booking" && item.code && (
              <span className="font-mono text-xs text-[#AC5D45]">PRENOTAZIONE {item.code}</span>
            )}
            {item.source === "standalone" && (
              <span
                className="text-[10px] uppercase bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded"
                title="Richiesta navetta inviata dal sito senza una prenotazione collegata"
              >
                senza prenotazione
              </span>
            )}
          </div>
          <div className="font-semibold text-[#1E3A5F] hover:text-[#AC5D45] transition">
            {item.guest_name || "—"}
          </div>
          <div className="text-xs text-[#635F59] flex flex-wrap gap-3 mt-1">
            {item.guest_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{item.guest_email}</span>}
            {item.guest_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{item.guest_phone}</span>}
            {item.check_in && <span className="flex items-center gap-1"><CalendarRange className="w-3 h-3" />{formatDate(item.check_in)} → {formatDate(item.check_out)}</span>}
            {item.created_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDateTime(item.created_at)}</span>}
          </div>
          {item.driver_name && (
            <div className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
              <Car className="w-3 h-3" /> Autista: <b>{item.driver_name}</b>
              {item.driver_phone && <span>· {item.driver_phone}</span>}
            </div>
          )}
        </div>
        {isPending && (
          <div className="flex gap-2">
            <Button onClick={onApprove} data-testid={`shuttle-approve-${item.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Check className="w-4 h-4 mr-1" /> Approva
            </Button>
            <Button onClick={onReject} variant="outline" data-testid={`shuttle-reject-${item.id}`} className="border-rose-300 text-rose-700 hover:bg-rose-50">
              <X className="w-4 h-4 mr-1" /> Rifiuta
            </Button>
          </div>
        )}
      </div>

      {/* TRIPS */}
      <div className="mt-3 grid gap-2">
        {(item.trips || []).map((t, i) => (
          <div key={`${t.date || ""}-${t.time || ""}-${t.origin || t.from || ""}-${i}`} className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1 font-medium text-[#1E3A5F]">
              <MapPin className="w-3.5 h-3.5" />
              {t.from || t.origin || "—"} → {t.to || t.destination || "—"}
            </span>
            <span className="text-xs text-[#635F59]">📅 {t.date || "—"}</span>
            <span className="text-xs text-[#635F59]">🕒 {t.time || "—"}</span>
            {t.passengers != null && <span className="text-xs text-[#635F59]">👥 {t.passengers}</span>}
            {t.luggage != null && <span className="text-xs text-[#635F59]">🧳 {t.luggage}</span>}
          </div>
        ))}
      </div>

      {item.admin_note && (
        <div className="mt-3 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-lg p-2 flex items-start gap-2">
          <MessageSquare className="w-3.5 h-3.5 mt-0.5" />
          <div><b>Nota host:</b> {item.admin_note}</div>
        </div>
      )}

      {item.notes && (
        <div className="mt-2 text-xs text-[#635F59]"><b>Note cliente:</b> {item.notes}</div>
      )}

      {item.estimated_total != null && (
        <div className="mt-2 text-xs text-[#AC5D45] font-semibold">Totale stimato: € {item.estimated_total}</div>
      )}

      {/* Actions row: complete / archive / delete (only for non-pending items) */}
      {!isPending && (
        <div className="mt-3 pt-3 border-t border-[#F0E9DD] flex flex-wrap gap-2 justify-end" onClick={stop}>
          {!isCompleted && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { stop(e); onComplete?.(item); }}
              data-testid={`shuttle-complete-${item.id}`}
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              title="Segna come effettuata (l'autista ha portato l'ospite)"
            >
              <Check className="w-4 h-4 mr-1" /> Fatta
            </Button>
          )}
          {isCompleted && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 px-2 py-1 rounded bg-emerald-50 border border-emerald-200">
              <Check className="w-3.5 h-3.5" /> Effettuata
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { stop(e); onArchive?.(item); }}
            data-testid={`shuttle-archive-${item.id}`}
            className="border-[#E8E2D9] text-[#635F59] hover:bg-[#FDFBF7]"
            title="Archivia (la rimuove dalla lista visibile)"
          >
            🗂 Archivia
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { stop(e); onDelete?.(item); }}
            data-testid={`shuttle-delete-${item.id}`}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            title="Elimina definitivamente"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Elimina
          </Button>
        </div>
      )}
    </div>
  );
};

const ShuttleDetailView = ({ item, onApprove, onReject, onClose }) => {
  const isPending = item.status === "pending" || item.status === "pending_host_approval";
  const waLink = buildWhatsAppLink(item);
  return (
    <div className="space-y-4 text-sm">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Car className="w-5 h-5" />
          Dettaglio richiesta navetta
          <Badge className={STATUS_BADGE[item.status] || "bg-slate-100"} variant="outline">
            {STATUS_LABEL[item.status] || item.status}
          </Badge>
        </DialogTitle>
      </DialogHeader>

      <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {item.source === "booking" && item.code && (
            <span className="font-mono text-xs text-[#AC5D45]">PRENOTAZIONE {item.code}</span>
          )}
          {item.source === "standalone" && (
            <span className="text-[10px] uppercase bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded">
              senza prenotazione
            </span>
          )}
        </div>
        <div className="font-semibold text-[#1E3A5F]">{item.guest_name}</div>
        <div className="text-xs text-[#635F59] flex flex-wrap gap-3 mt-1">
          {item.guest_email && <span>📧 {item.guest_email}</span>}
          {item.guest_phone && (
            <a href={`tel:${item.guest_phone}`} className="text-[#1E3A5F] hover:underline">
              📱 {item.guest_phone}
            </a>
          )}
        </div>
      </div>

      {/* TRIPS with calendar links per trip */}
      <div>
        <div className="text-xs uppercase tracking-wider text-[#635F59] mb-2">Tratte</div>
        <div className="grid gap-3">
          {(item.trips || []).map((t, i) => {
            const gcal = buildGoogleCalendarLink(t, item);
            const ics = buildIcsDataUrl(t, item);
            return (
              <div key={`${t.date || ""}-${t.time || ""}-${t.origin || t.from || ""}-${i}`} className="bg-white border border-[#E8E2D9] rounded-lg p-3">
                <div className="font-medium text-[#1E3A5F] flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t.from || t.origin} → {t.to || t.destination}
                </div>
                <div className="text-xs text-[#635F59] mt-1">
                  📅 {t.date} · 🕒 {t.time} · 👥 {t.passengers || 1} · 🧳 {t.luggage || 0}
                </div>
                {item.status === "approved" && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {gcal && (
                      <a
                        href={gcal}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#E8E2D9] hover:bg-[#FDFBF7]"
                        data-testid={`gcal-link-${i}`}
                      >
                        <CalendarPlus className="w-3 h-3" /> Google Calendar
                      </a>
                    )}
                    {ics && (
                      <a
                        href={ics}
                        download={`navetta-${item.id}-${i}.ics`}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#E8E2D9] hover:bg-[#FDFBF7]"
                        data-testid={`ics-link-${i}`}
                      >
                        <CalendarPlus className="w-3 h-3" /> Apple Calendar (.ics)
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {item.driver_name && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-emerald-800 mb-1">Autista assegnato</div>
          <div className="font-semibold text-emerald-900">{item.driver_name}</div>
          <div className="text-xs text-emerald-700">
            📱 {item.driver_phone}{item.driver_vehicle ? ` · 🚐 ${item.driver_vehicle}` : ""}
          </div>
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea655] text-white px-3 py-1.5 rounded-md text-xs font-semibold"
              data-testid="whatsapp-driver-btn"
            >
              <Send className="w-3.5 h-3.5" /> Inoltra all'autista su WhatsApp
            </a>
          )}
        </div>
      )}

      {item.admin_note && (
        <div className="text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-lg p-2">
          <b>Nota host:</b> {item.admin_note}
        </div>
      )}
      {item.notes && (
        <div className="text-xs text-[#635F59]"><b>Note cliente:</b> {item.notes}</div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Chiudi</Button>
        {isPending && (
          <>
            <Button onClick={onReject} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50">
              <X className="w-4 h-4 mr-1" /> Rifiuta
            </Button>
            <Button onClick={onApprove} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Check className="w-4 h-4 mr-1" /> Approva
            </Button>
          </>
        )}
      </DialogFooter>
    </div>
  );
};

// ============================================================
// DRIVERS MANAGER (Rubrica Autisti)
// ============================================================
const emptyDriver = { name: "", phone: "", vehicle: "", note: "" };
const DriversManager = ({ drivers, onChange }) => {
  const [editing, setEditing] = useState(null); // driver being edited (null = adding new)
  const [form, setForm] = useState(emptyDriver);
  const [saving, setSaving] = useState(false);

  const startNew = () => { setEditing("new"); setForm(emptyDriver); };
  const startEdit = (d) => {
    setEditing(d.id);
    setForm({ name: d.name, phone: d.phone, vehicle: d.vehicle || "", note: d.note || "" });
  };
  const cancel = () => { setEditing(null); setForm(emptyDriver); };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nome e telefono sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/drivers", form);
        toast.success("Autista aggiunto");
      } else {
        await api.put(`/admin/drivers/${editing}`, form);
        toast.success("Autista aggiornato");
      }
      cancel();
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare l'autista "${d.name}"?`)) return;
    try {
      await api.delete(`/admin/drivers/${d.id}`);
      toast.success("Autista eliminato");
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione"));
    }
  };

  return (
    <div className="bg-white border border-[#E8E2D9] rounded-xl p-5" data-testid="drivers-manager">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#1E3A5F] flex items-center gap-2">
          <Users className="w-4 h-4" /> Rubrica Autisti
        </h4>
        {editing === null && (
          <Button size="sm" onClick={startNew} data-testid="driver-add-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Aggiungi autista
          </Button>
        )}
      </div>

      {editing !== null && (
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 mb-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input
            placeholder="Nome e cognome *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="driver-form-name"
          />
          <Input
            placeholder="Telefono (con prefisso, es. +39…) *"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="driver-form-phone"
          />
          <Input
            placeholder="Veicolo (es. Fiat Ducato blu — opzionale)"
            value={form.vehicle}
            onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
            data-testid="driver-form-vehicle"
          />
          <Input
            placeholder="Note (opzionale)"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            data-testid="driver-form-note"
          />
          <div className="md:col-span-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={cancel} disabled={saving}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="driver-form-save">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </div>
        </div>
      )}

      {drivers.length === 0 ? (
        <p className="text-sm text-[#635F59]">Nessun autista in rubrica. Aggiungi il primo per poter approvare le navette.</p>
      ) : (
        <ul className="divide-y divide-[#E8E2D9]">
          {drivers.map((d) => (
            <li key={d.id} className="py-2 flex items-center justify-between gap-2" data-testid={`driver-row-${d.id}`}>
              <div className="text-sm">
                <div className="font-semibold text-[#1E3A5F]">{d.name}</div>
                <div className="text-xs text-[#635F59]">
                  📱 {d.phone}{d.vehicle ? ` · 🚐 ${d.vehicle}` : ""}{d.note ? ` · ${d.note}` : ""}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => startEdit(d)} data-testid={`driver-edit-${d.id}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(d)} data-testid={`driver-delete-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
